import React, { useState } from 'react';
import { FileText, ShoppingCart } from 'lucide-react';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';
import Button from './Button';
import StatusBadge from './StatusBadge';
import { ProductHeader, PriceBlock, QuantityInput, InCartNote } from './ProductParts';

// Assembly (BOM) product. Shares its header, price, quantity and cart pieces
// with ProductDetailView so an assembly reads as the same kind of page as a part.
const BOMComponent = ({
  bom,
  addToCart,
  canViewComponents,
  isFavourite,
  onToggleFavourite,
  onAddToInquiry,
  inCartQty = 0,
  onViewCart,
}) => {
  const [buildQty, setBuildQty] = useState(1);
  const { enabled: orderingEnabled } = useOrderingEnabled();
  const [availabilityCheck, setAvailabilityCheck] = useState(null);

  const changeBuildQty = (qty) => {
    setBuildQty(qty);
    // A check belongs to the quantity it was run for.
    setAvailabilityCheck(null);
  };

  const checkAvailability = () => {
    const availability = bom.components.map(component => {
      const requiredQty = component.quantity * buildQty;
      const available = component.qty_available >= requiredQty;
      return {
        part_number: component.part_number,
        description: component.description,
        required: requiredQty,
        available: component.qty_available,
        status: available ? 'Available' : 'Insufficient',
      };
    });

    const canBuild = availability.every(item => item.status === 'Available');
    setAvailabilityCheck({ canBuild, components: availability });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (orderingEnabled && addToCart) addToCart(bom, buildQty);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <ProductHeader
        code={bom.bom_code}
        description={bom.bom_description}
        isFavourite={isFavourite}
        onToggleFavourite={onToggleFavourite}
      >
        <StatusBadge className="mt-2">Assembly</StatusBadge>
      </ProductHeader>

      <div className="p-6 space-y-6">
        <PriceBlock price={bom.bom_buy_price} listPrice={bom.bom_base_price} />

        <div className="pt-6 border-t border-gray-200 space-y-4">
          <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
            <QuantityInput id="buildQty" label="Build quantity" value={buildQty} onChange={changeBuildQty} />
            {orderingEnabled && addToCart ? (
              <Button
                type="submit"
                size="lg"
                className="font-semibold"
                icon={<ShoppingCart className="w-4 h-4" aria-hidden="true" />}
              >
                Add {buildQty} to Cart
              </Button>
            ) : (
              <p className="text-sm font-medium text-gray-700 py-3">Online ordering is currently unavailable</p>
            )}
            <Button variant="secondary" size="lg" onClick={checkAvailability}>
              Check Availability
            </Button>
          </form>

          <InCartNote quantity={inCartQty} onViewCart={onViewCart} />

          {onAddToInquiry && (
            <Button
              variant="secondary"
              onClick={onAddToInquiry}
              icon={<FileText className="w-4 h-4" aria-hidden="true" />}
            >
              Add to Inquiry
            </Button>
          )}
        </div>

        {availabilityCheck && (
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50" role="status">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-900">Availability check</h2>
              <StatusBadge tone={availabilityCheck.canBuild ? 'success' : 'danger'}>
                {availabilityCheck.canBuild ? 'Can build' : 'Cannot build'} {buildQty} {buildQty === 1 ? 'unit' : 'units'}
              </StatusBadge>
            </div>
            {canViewComponents && (
              <ul className="mt-3 space-y-2">
                {availabilityCheck.components.map((item, index) => (
                  <li key={index} className="flex flex-wrap justify-between gap-x-4 text-sm">
                    <span className="text-gray-900">
                      <span className="font-mono">{item.part_number}</span> - {item.description}
                    </span>
                    <span className={`tabular-nums ${item.status === 'Available' ? 'text-green-700' : 'text-red-700 font-medium'}`}>
                      Required {item.required}, available {item.available}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {canViewComponents && (
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-3">Components</h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left font-medium">Part Number</th>
                    <th scope="col" className="px-4 py-2 text-left font-medium">Description</th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">Quantity</th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bom.components.map((component, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 font-mono text-gray-900 whitespace-nowrap">{component.part_number}</td>
                      <td className="px-4 py-2 text-gray-900">{component.description}</td>
                      <td className="px-4 py-2 text-right text-gray-900 tabular-nums">{component.quantity}</td>
                      <td className="px-4 py-2 text-right text-gray-900 tabular-nums">{component.qty_available}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {bom.note && <p className="text-sm text-gray-600">{bom.note}</p>}
      </div>
    </div>
  );
};

export default BOMComponent;
